// Step-to-step hand-offs inside the start flow — the reel advance.
//
// Used for hero → draft and draft → tavern. Both are structurally the same
// move: the player has just committed a choice and the run steps forward one
// frame. One sequence serves both.
//
// Concept: the film advances. A single opaque panel sweeps across the frame in
// one continuous direction with a hot frame-line on its leading edge — the
// outgoing step leaves with it and the incoming step is simply what's there
// when it has passed. That reads as one reel rather than four unrelated
// screens, which is the point of using the tape language throughout.
//
// LENGTH IS THE WHOLE DESIGN HERE
// -------------------------------
// This is not a showcase. It punctuates a step the player takes deliberately,
// two or three times per run, while they are mid-decision and want to get on
// with it. At 0.45s it is half the FIGHT intro and under a third of the
// Welcome → Menu transport. If it ever feels like a *moment*, it is too long.

import type { MotionTier } from './motion';
import { type SequenceScore, scaleSequence, devSpeedFactor } from './sequence';

export interface AdvanceScore extends SequenceScore {
  /** Sweep direction. 1 = panel travels right-to-left, -1 = left-to-right. */
  direction: 1 | -1;
}

const FULL: AdvanceScore = {
  tier: 'full',
  cover: { at: 0.0, dur: 0.16 },
  commitAt: 0.22,
  settle: { at: 0.28, dur: 0.17 },
  total: 0.45,
  travels: true,
  direction: 1,
};

const LOW_POWER: AdvanceScore = {
  tier: 'lowPower',
  cover: { at: 0.0, dur: 0.13 },
  commitAt: 0.18,
  settle: { at: 0.22, dur: 0.14 },
  total: 0.36,
  travels: true,
  direction: 1,
};

/**
 * Reduced motion. The panel doesn't sweep — it cross-dissolves in place. Still a
 * deliberate covered hand-off rather than a cut, so the step still reads as a
 * step.
 */
const REDUCED: AdvanceScore = {
  tier: 'reduced',
  cover: { at: 0.0, dur: 0.1 },
  commitAt: 0.14,
  settle: { at: 0.18, dur: 0.1 },
  total: 0.28,
  travels: false,
  direction: 1,
};

export interface AdvanceOptions {
  /** Reverse the sweep — used when stepping backward through the flow. */
  reverse?: boolean;
}

export function buildAdvanceScore(
  tier: MotionTier,
  opts: AdvanceOptions = {},
): AdvanceScore {
  const base = tier === 'reduced' ? REDUCED : tier === 'lowPower' ? LOW_POWER : FULL;
  const directed: AdvanceScore = opts.reverse ? { ...base, direction: -1 } : base;
  return scaleSequence(directed, devSpeedFactor());
}
